import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useMutation} from '@github-ui/react-query'
import {
  useMarkersActions,
  useGetThread,
  usePendingReviewActions,
  usePullRequestBasePath,
  useOnCommentThreadDeleted,
  useUnifiedBatchActions,
} from '../stores/layout-selectors'
import type {FileKey, LineKey} from '@github-ui/diff-lines/types'
import {useThreadCountActions} from '../page-data/loaders/use-tab-counts'

export function useDeleteReviewCommentMutation({
  filePath,
  lineMarkersKey,
  onSuccess,
}: {
  filePath: string
  lineMarkersKey: LineKey | FileKey
  onSuccess?: () => void
}) {
  const basePath = usePullRequestBasePath()
  const {deleteThreadComment} = useMarkersActions()
  const {countDeletedThread} = useThreadCountActions()
  const onCommentThreadDeleted = useOnCommentThreadDeleted()
  const getThread = useGetThread()
  const {removePendingComment} = usePendingReviewActions()
  const {removeItemsForComment} = useUnifiedBatchActions()

  return useMutation({
    mutationFn: async ({commentId, threadId}: {commentId: string; threadId: string; filePath: string}) => {
      const comment = getThread(Number(threadId), filePath, lineMarkersKey)?.commentsData?.comments?.find(
        c => c.id === commentId,
      )
      if (!comment) return

      const apiUrl = `${basePath}/page_data/review_comments/${comment.databaseId}`
      const response = await fetchWithErrorHandling(`${apiUrl}`, {
        method: 'DELETE',
      })

      if (response.status === 204 || response.status === 200) {
        return comment.databaseId
      }

      // If we don't return a 204 (no content), we need to parse the response
      // and render the errors
      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
    },

    onSuccess: (deletedCommentDatabaseId, {commentId, threadId}) => {
      const threadWasResolved = getThread(Number(threadId), filePath, lineMarkersKey)?.isResolved ?? false

      if (deletedCommentDatabaseId != null) {
        removeItemsForComment(deletedCommentDatabaseId)
      }

      const result = deleteThreadComment(Number(threadId), commentId, filePath, lineMarkersKey)

      removePendingComment(Number(threadId))

      if (result !== 'thread_deleted') {
        return
      }

      if (result === 'thread_deleted') {
        countDeletedThread(threadWasResolved)

        onCommentThreadDeleted?.({
          path: filePath,
          threadID: threadId,
        })
      }

      onSuccess?.()
    },
  })
}
